import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { trackConversion } from "@/lib/conversionTracking";
import type { PaywallPlan } from "@/types/paywall";

const JUST_UPGRADED_KEY = "reviewboost_just_upgraded";

declare global {
  interface Window {
    __LENCO_CONFIG__?: { publicKey?: string };
  }
}

/** Lenco public key: (1) env, (2) window.__LENCO_CONFIG__, (3) get-payment-public-key. Sync when key is local. */
function getLencoPublicKeySync(): string {
  const fromEnv = (import.meta.env.VITE_LENCO_PUBLIC_KEY as string)?.trim() ?? "";
  if (fromEnv) return fromEnv;
  return (typeof window !== "undefined" && window.__LENCO_CONFIG__?.publicKey as string)?.trim() ?? "";
}

async function getLencoPublicKey(supabaseClient: { functions: { invoke: (name: string) => Promise<{ data?: { publicKey?: string }; error?: unknown }> } }): Promise<string> {
  const sync = getLencoPublicKeySync();
  if (sync) return sync;
  const { data, error } = await supabaseClient.functions.invoke("get-payment-public-key");
  if (error) return "";
  return (data?.publicKey as string)?.trim() ?? "";
}

function getAmountZMW(opts: LencoPaymentOptions): number {
  const { plan, isAnnual, isFounding } = opts;
  if (isAnnual) return plan === "pro_solo" ? 8500 : 17000;
  if (plan === "pro_solo") return isFounding ? 750 : 900;
  return isFounding ? 1400 : 1800;
}

export interface LencoPaymentOptions {
  plan: PaywallPlan;
  isAnnual: boolean;
  isFounding: boolean;
}

const DEFAULT_OPTIONS: LencoPaymentOptions = {
  plan: "pro_solo",
  isAnnual: false,
  isFounding: false,
};

export function useLencoPayment() {
  const { user, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(false);

  const startPayment = useCallback(
    async (opts: LencoPaymentOptions = DEFAULT_OPTIONS) => {
      if (!user) return;

      setLoading(true);
      let widgetOpened = false;
      try {
        const lencoKey = getLencoPublicKeySync() || (await getLencoPublicKey(supabase));
        if (!lencoKey) {
          alert("Payment is not configured. Set LENCO_PUBLIC_KEY in Supabase Edge Function secrets.");
          return;
        }
        if (!window.LencoPay) {
          alert("Payment service is loading. Please try again.");
          return;
        }
        widgetOpened = true;
        const amount = getAmountZMW(opts);
        const reference = "RB-" + Date.now();
        window.LencoPay.getPaid({
          key: lencoKey,
          reference,
          email: user.email ?? "",
          amount,
          currency: "ZMW",
          channels: ["card", "mobile-money"],
          onSuccess: async (response: { reference: string }) => {
            try {
              const { error } = await supabase.functions.invoke("lenco-webhook", {
                body: {
                  reference: response.reference,
                  plan: opts.plan,
                  isAnnual: opts.isAnnual,
                  isFounding: opts.isFounding,
                },
              });
              if (!error) {
                trackConversion("upgrade_completed", {
                  reference: response.reference,
                  plan: opts.plan,
                  billing: opts.isAnnual ? "annual" : "monthly",
                  founding: opts.isFounding,
                });
                try {
                  window.sessionStorage.setItem(JUST_UPGRADED_KEY, "1");
                } catch {
                  /* ignore */
                }
                await refreshProfile();
                if (typeof window !== "undefined") window.location.reload();
              } else {
                alert("Verification failed. Please contact support.");
              }
            } catch (err) {
              console.error(err);
              alert("Verification failed. Please contact support.");
            } finally {
              setLoading(false);
            }
          },
          onClose: () => setLoading(false),
        });
      } catch (error) {
        console.error(error);
        alert("Cannot start payment. Please try again.");
      } finally {
        if (!widgetOpened) setLoading(false);
      }
    },
    [user, refreshProfile]
  );

  return { startPayment, loading };
}

export { JUST_UPGRADED_KEY };

